import {
  IDocuSignEnvelopeTemplate,
  IExperienceTemplateResource,
  ISubTaskDocuSignEnvelopeSignTemplate,
  ISupportingActor,
  TemplateResourceType
} from '@dadohr/shared/domainTypes/experience';
import {
  DocuSignEnvelopeTabMapping,
  DocuSignRecipient,
  DocuSignRecipientWithActor,
  DocuSignTab,
  DocuSignTemplate,
  DocuSignTemplateWithExtracedFillData
} from '@dadohr/shared/domainTypes/integrations/docusign';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Tooltip,
  Typography
} from '@material-ui/core';
import { compact, flatten, keyBy, sortBy } from 'lodash';
import { ExternalLink, Plus, PlusCircle, X } from 'lucide-react';
import React, { SetStateAction, useMemo, useState } from 'react';
import shortid from 'shortid';
import { AutocompleteSingle } from '../../../../../../../../components/Autocomplete';
import { ButtonWithPromise } from '../../../../../../../../components/ButtonWithPromise';
import { WithShape } from '../../../../../../../../components/Charts/Util';
import { CustomInputLabel } from '../../../../../../../../components/CustomInputLabel';
import { DialogTitleWithTypography } from '../../../../../../../../components/DialogTitleWithTypography';
import { Loader } from '../../../../../../../../components/Loader';
import {
  MarkdownEditorWithReplacementTokens,
  TokenDefinition
} from '../../../../../../../../components/MarkdownText';
import { SelectCustom } from '../../../../../../../../components/SelectCustom';
import { TextFieldReadOnly } from '../../../../../../../../components/TextFieldReadOnly';
import { useUnsafeTranslation } from '../../../../../../../../i18next';
import { Centered } from '../../../../../../../../layout/Centered';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../../../layout/Flex';
import { joinWithCommaAndAnd } from '../../../../../../../../services/enumerate';
import { useFirebaseFunction } from '../../../../../../../../services/firebaseFunctions';
import { LightMdToJsx } from '../../../../../../../../services/formatter';
import { ONE_MINUTE } from '../../../../../../../../services/time';
import {
  compositeUserFields,
  toSearchTermRegexp,
  useUserFields
} from '../../../../../../../../services/user';
import {
  DadoFieldOption,
  HIDDEN_USER_FIELD_KEYS
} from '../../../../../../../Users/pages/Imports/components/CustomImportMappingForm';
import {
  ActorDescriptor,
  useActorDescriptorsFromTemplate
} from '../../../../../Actors';
import { BlockSettingsSubPage, TaskBuilderContext } from '../../../types';
import {
  TaskBuilderBlockValidationState,
  TaskBuilderBlockValidationStateItem
} from '../../../validation';

const OUTER_SPACING = 4;
const INNER_SPACING = 2;
const TITLE_SPACING = 2;

const useDocuSignEnvelopeTemplates = (orgId: string) => {
  return useFirebaseFunction<DocuSignTemplate[]>(
    'integrations_docusign-listTemplates',
    { orgId },
    [orgId],
    ONE_MINUTE * 15 // TODO DocuSign - long in development
  );
};

const useDocuSignEnvelopeTemplate = (orgId: string, tplId: string) => {
  return useFirebaseFunction<DocuSignTemplateWithExtracedFillData>(
    'integrations_docusign-getTemplate',
    { orgId, tplId },
    [orgId, tplId],
    ONE_MINUTE * 15 // TODO DocuSign - long in development
  );
};

const ResourceSelector = ({
  value,
  onChange,
  options,
  onRequestCreateNewResource
}: {
  value: IDocuSignEnvelopeTemplate | null;
  onChange: (nextValue: IDocuSignEnvelopeTemplate | null) => void;
  options: IDocuSignEnvelopeTemplate[];
  onRequestCreateNewResource: () => void;
}) => {
  const { t } = useUnsafeTranslation();

  return (
    <CustomInputLabel
      fullWidth
      label={t('Docusign Template')}
      otherElements={
        <FlexContainer justifyContent="flex-end" fullWidth>
          <Tooltip title={`${t('Configure a new template')}`}>
            <IconButton
              color="primary"
              onClick={() => onRequestCreateNewResource()}
            >
              <Plus size={16} />
            </IconButton>
          </Tooltip>
        </FlexContainer>
      }
    >
      <AutocompleteSingle
        fullWidth
        value={value}
        options={options}
        disabledClearable
        getOptionLabel={(o) => o?.name || ''}
        getOptionSelected={(o, v) => o?.resourceId === v.resourceId}
        onChange={(nextValue) => onChange(nextValue || null)}
        renderOption={(o) => o?.name || ''}
        filterOptions={(os, s) => {
          const t = toSearchTermRegexp(s.inputValue);
          return os.filter((o) => o && t.exec(o.name));
        }}
      />
    </CustomInputLabel>
  );
};

const TemplateSelector = ({
  value,
  onChange,
  options,
  label
}: {
  value: string;
  onChange: (nextValue: DocuSignTemplate | null) => void;
  options: DocuSignTemplate[];
  label?: React.ReactNode;
}) => {
  const { t } = useUnsafeTranslation();
  const selectedOption = options.find((o) => o.id === value);

  return (
    <AutocompleteSingle
      label={label}
      fullWidth={true}
      value={selectedOption}
      options={options}
      getOptionLabel={(o) => o?.name || ''}
      getOptionSelected={(o, v) => !!(o && v && o?.id === v?.id)}
      onChange={(nextValue) => onChange(nextValue || null)}
      renderOption={(o) => o?.name || ''}
      filterOptions={(os, s) => {
        const t = toSearchTermRegexp(s.inputValue);
        return os.filter((o) => o && t.exec(o.name));
      }}
    />
  );
};

const ActorSelector = ({
  value,
  onChange,
  options,
  label
}: {
  value: string;
  onChange: (nextValue: string) => void;
  options: ActorDescriptor[];
  label?: React.ReactNode;
}) => {
  const selectedOption = options.find((a) => a.key === value) || null;
  return (
    <AutocompleteSingle
      label={label}
      fullWidth
      value={selectedOption}
      disabledClearable
      options={options}
      getOptionLabel={(o) => o?.label || ''}
      renderOption={(o) => (
        <WithShape shape="square" small color={o.color}>
          {o.label}
        </WithShape>
      )}
      getOptionSelected={(o) => o.key === selectedOption?.key}
      onChange={(o) => o && onChange(o.key)}
      filterOptions={(os, s) => {
        const t = toSearchTermRegexp(s.inputValue);
        return os.filter((o) => o && t.exec(o.label));
      }}
    />
  );
};

const DadoKeySelector = ({
  value,
  onChange,
  customUserFields,
  label
}: {
  value: string;
  onChange: (nextValue: string) => void;
  customUserFields: CustomUserField[];
  label?: React.ReactNode;
}) => {
  const { t } = useUnsafeTranslation();
  const userFields = useUserFields(customUserFields);
  const options: DadoFieldOption[] = useMemo(
    () =>
      sortBy(
        [...compositeUserFields(['$FULL_NAME$'], t), ...userFields].filter(
          (f) => !HIDDEN_USER_FIELD_KEYS.includes(f.key as any)
        ),
        (f) => f.label
      ),
    [userFields, t]
  );

  const selectedOption = options.find((a) => a.key === value) || null;
  return (
    <AutocompleteSingle
      fullWidth
      label={label}
      disabledClearable
      value={selectedOption}
      options={options}
      getOptionLabel={(o) => o.label}
      getOptionSelected={(o) => o.key === selectedOption?.key}
      onChange={(o) => o && onChange(o.key)}
      renderOption={(o) => o?.label || ''}
      filterOptions={(os, s) => {
        const t = toSearchTermRegexp(s.inputValue);
        return os.filter((o) => o && t.exec(o.label));
      }}
    />
  );
};

const MappingsGrid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr max-content',
  gridColumnGap: p.theme.spacing(1),
  gridRowGap: p.theme.spacing(1),
  width: '100%',
  alignItems: 'center'
}));

const updateMapping = (
  ms: DocuSignEnvelopeTabMapping[],
  update: DocuSignEnvelopeTabMapping | null,
  i: number
) => {
  return compact(ms.map((m, oi) => (oi === i ? update : m)));
};

const EmailFieldSelector = ({
  value,
  onChange,
  label
}: {
  value: string;
  onChange: (nextValue: string) => void;
  label?: React.ReactNode;
}) => {
  const { t } = useUnsafeTranslation();
  const options = [
    {
      label: t('Work Email'),
      key: 'email'
    },
    { label: t('Personal Email'), key: 'personalEmail' }
  ];

  const selectedOption = options.find((o) => o.key === value) || null;

  return (
    <AutocompleteSingle
      fullWidth
      label={label}
      disabledClearable
      value={selectedOption}
      options={options}
      getOptionLabel={(o) => o.label}
      getOptionSelected={(o) => o?.key === ''}
      onChange={(o) => o && onChange(o.key)}
      renderOption={(o) => o?.label || ''}
      filterOptions={(os, s) => {
        const t = toSearchTermRegexp(s.inputValue);
        return os.filter((o) => o && t.exec(o.label));
      }}
    />
  );
};

const SignerRolesSelectorGrid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gridColumnGap: p.theme.spacing(1),
  gridRowGap: p.theme.spacing(1),
  width: '100%',
  alignItems: 'center'
}));

const SignerRolesSelector = ({
  value,
  onChange,
  actorDescriptors
}: {
  value: IDocuSignEnvelopeTemplate;
  onChange: (nextValue: IDocuSignEnvelopeTemplate) => void;
  actorDescriptors: ActorDescriptor[];
}) => {
  const { t } = useUnsafeTranslation();
  const updateSigner = (nextSigner: DocuSignRecipientWithActor) => {
    onChange({
      ...value,
      signers: value.signers.map((v) =>
        v.recipientId === nextSigner.recipientId ? nextSigner : v
      )
    });
  };

  return (
    <SignerRolesSelectorGrid>
      <Typography variant="caption">
        {t('Role in Docusign template')}
      </Typography>
      <Typography variant="caption">{t('Role in Dado experience')}</Typography>
      <Typography variant="caption">{t('Email')}</Typography>
      {value.signers.map((v) => (
        <React.Fragment key={v.recipientId}>
          <TextFieldReadOnly value={v.roleName} />
          <ActorSelector
            value={v.actor.key}
            onChange={(nextActorKey) => {
              updateSigner({
                ...v,
                actor: {
                  ...v.actor,
                  key: nextActorKey,
                  emailField: v.actor.emailField || 'email'
                }
              });
            }}
            options={actorDescriptors}
          />
          <EmailFieldSelector
            value={v.actor.emailField}
            onChange={(nextEmailField) => {
              updateSigner({
                ...v,
                actor: {
                  ...v.actor,
                  emailField: nextEmailField
                }
              });
            }}
          />
        </React.Fragment>
      ))}
    </SignerRolesSelectorGrid>
  );
};

const OtherRolesSelectorGrid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr max-content',
  gridColumnGap: p.theme.spacing(1),
  gridRowGap: p.theme.spacing(1),
  width: '100%',
  alignItems: 'center'
}));

const OtherRolesSelector = ({
  value,
  onChange,
  actorDescriptors,
  otherRoles
}: {
  value: IDocuSignEnvelopeTemplate;
  onChange: (nextValue: IDocuSignEnvelopeTemplate) => void;
  actorDescriptors: ActorDescriptor[];
  otherRoles: {
    ccs: DocuSignRecipient[];
  };
}) => {
  const { t } = useUnsafeTranslation();

  const updateCc = (nextCc: DocuSignRecipientWithActor, i: number) => {
    onChange({
      ...value,
      ccs: value.ccs.map((v, otherI) => (otherI === i ? nextCc : v))
    });
  };

  const remainingRoles = otherRoles.ccs.filter(
    (cc) => !value.ccs.find((occ) => occ.recipientId === cc.recipientId)
  );

  return (
    <FlexContainerVertical spacing={2}>
      {!!value.ccs.length && (
        <OtherRolesSelectorGrid>
          <Typography variant="caption">
            {t('Role in Docusign template')}
          </Typography>
          <Typography variant="caption">
            {t('Role in Dado experience')}
          </Typography>
          <Typography variant="caption">{t('Email')}</Typography>
          <div />
          {value.ccs.map((v, i) => {
            const selectedRole =
              otherRoles.ccs.find((cc) => cc.recipientId === v.recipientId) ||
              null;
            return (
              <React.Fragment key={v.recipientId}>
                {remainingRoles.length > 0 ? (
                  <AutocompleteSingle
                    fullWidth
                    value={selectedRole}
                    disabledClearable
                    options={[...remainingRoles, selectedRole]}
                    getOptionLabel={(o) => (o ? `CC - ${o.roleName}` : '')}
                    renderOption={(o) => (o ? `CC - ${o.roleName}` : '')}
                    getOptionSelected={(o) => o?.recipientId === v.recipientId}
                    onChange={(o) =>
                      o &&
                      updateCc(
                        {
                          ...v,
                          roleName: o.roleName,
                          recipientId: o.recipientId
                        },
                        i
                      )
                    }
                    filterOptions={(os, s) => {
                      const t = toSearchTermRegexp(s.inputValue);
                      return os.filter((o) => o && t.exec(o.roleName));
                    }}
                  />
                ) : (
                  <TextFieldReadOnly value={`${t('CC')} - ${v.roleName}`} />
                )}
                <ActorSelector
                  value={v.actor.key}
                  onChange={(nextActorKey) => {
                    updateCc(
                      {
                        ...v,
                        actor: {
                          ...v.actor,
                          key: nextActorKey,
                          emailField: v.actor.emailField || 'email'
                        }
                      },
                      i
                    );
                  }}
                  options={actorDescriptors}
                />
                <EmailFieldSelector
                  value={v.actor.emailField}
                  onChange={(nextEmailField) => {
                    updateCc(
                      {
                        ...v,
                        actor: {
                          ...v.actor,
                          emailField: nextEmailField
                        }
                      },
                      i
                    );
                  }}
                />
                <IconButton>
                  <X
                    size={16}
                    onClick={() => {
                      onChange({
                        ...value,
                        ccs: value.ccs.filter(
                          (cc) => cc.recipientId !== v.recipientId
                        )
                      });
                    }}
                  />
                </IconButton>
              </React.Fragment>
            );
          })}
        </OtherRolesSelectorGrid>
      )}
      {!!remainingRoles.length && (
        <Button
          size="small"
          color="primary"
          startIcon={<PlusCircle size={16} />}
          onClick={() => {
            const nextAvailableRole = remainingRoles[0];
            onChange({
              ...value,
              // preselect the first role that is available
              ccs: [
                ...value.ccs,
                {
                  actor: {
                    type: 'actor',
                    key: '',
                    emailField: '',
                    optional: false
                  },
                  recipientId: nextAvailableRole?.recipientId || '',
                  roleName: nextAvailableRole?.roleName || ''
                }
              ]
            });
          }}
        >
          {t('Add Recipient')}
        </Button>
      )}
    </FlexContainerVertical>
  );
};

const MappingsSelector = ({
  tpl,
  value,
  onChange,
  customFields,
  actors
}: {
  tpl: DocuSignTemplateWithExtracedFillData;
  value: IDocuSignEnvelopeTemplate;
  onChange: (nextValue: IDocuSignEnvelopeTemplate) => void;
  customFields: CustomUserField[];
  actors: { [key: string]: ActorDescriptor };
}) => {
  const { t } = useUnsafeTranslation();
  const allTabs = flatten(Object.values(tpl.tabs));
  const tabsById = keyBy(allTabs, (m) => m.tabId);
  const remainingTabs = allTabs.filter(
    (tab) => !value.mappings.find((m) => m.tabId === tab.tabId)
  );
  const isSingleSignerTemplate = tpl.tpl.signers.length === 1;

  const updateMappings = (
    update: DocuSignEnvelopeTabMapping | null,
    i: number
  ) => {
    onChange({
      ...value,
      mappings: updateMapping(value.mappings, update, i)
    });
  };

  const toTabLabel = (tab: DocuSignTab) => {
    return compact([
      isSingleSignerTemplate
        ? null
        : tpl.tpl.signers.find((s) => s.recipientId === tab.recipientId)
            ?.roleName || null,
      tab.tabLabel
    ]).join(' - ');
  };

  return (
    <FlexContainerVertical spacing={2}>
      <MappingsGrid>
        <Typography variant="caption">
          {t('Field in Docusign template')}
        </Typography>
        <Typography variant="caption">
          {t('Role in Dado experience')}
        </Typography>
        <Typography variant="caption">{t('Dado data field')}</Typography>
        <div />
        {value.mappings.map((mapping, i) => {
          const tab = tabsById[mapping.tabId];
          if (!tab) {
            return null;
          }
          return (
            <React.Fragment key={mapping.tabId}>
              {remainingTabs.length > 0 ? (
                <AutocompleteSingle
                  fullWidth
                  value={tab}
                  options={[...remainingTabs, tab]}
                  getOptionLabel={(o) => (o ? toTabLabel(o) : '')}
                  renderOption={(o) => (o ? toTabLabel(o) : '')}
                  getOptionSelected={(o) => o?.tabId === tab.tabId}
                  onChange={(o) =>
                    o &&
                    updateMappings(
                      {
                        ...mapping,
                        tabId: o.tabId,
                        tabLabel: o.tabLabel
                      },
                      i
                    )
                  }
                  filterOptions={(os, s) => {
                    const t = toSearchTermRegexp(s.inputValue);
                    return os.filter((o) => o && t.exec(o.tabLabel));
                  }}
                />
              ) : (
                <TextFieldReadOnly value={toTabLabel(tab)} />
              )}
              <ActorSelector
                value={mapping.actor}
                onChange={(nextActor) => {
                  updateMappings(
                    {
                      ...mapping,
                      actor: nextActor
                    },
                    i
                  );
                }}
                options={Object.values(actors)}
              />
              <DadoKeySelector
                value={mapping.dadoKey}
                onChange={(nextDadoKey) => {
                  updateMappings(
                    {
                      ...mapping,
                      dadoKey: nextDadoKey
                    },
                    i
                  );
                }}
                customUserFields={customFields}
              />

              <IconButton>
                <X
                  size={16}
                  onClick={() => {
                    updateMappings(null, i);
                  }}
                />
              </IconButton>
            </React.Fragment>
          );
        })}
      </MappingsGrid>

      {!!remainingTabs.length && (
        <Button
          size="small"
          color="primary"
          startIcon={<PlusCircle size={16} />}
          onClick={() => {
            const nextAvailableTab = remainingTabs[0];
            onChange({
              ...value,
              // preselect the first role that is available
              mappings: [...value.mappings, toEmptyMapping(nextAvailableTab)]
            });
          }}
        >
          {t('Add Field')}
        </Button>
      )}
    </FlexContainerVertical>
  );
};

const TemplateConfiguratorInner = ({
  tpl,
  value,
  onChange,
  customFields,
  supportingActors
}: {
  tpl: DocuSignTemplateWithExtracedFillData;
  value: IDocuSignEnvelopeTemplate;
  onChange: (nextValue: IDocuSignEnvelopeTemplate) => void;
  customFields: CustomUserField[];
  supportingActors: { [actorKey: string]: ISupportingActor };
}) => {
  const { t } = useUnsafeTranslation();
  // A channel actor has no email and can't sign or receive a document.
  const actors = useActorDescriptorsFromTemplate(supportingActors, false);

  return (
    <FlexContainerVertical fullWidth spacing={OUTER_SPACING}>
      <FlexContainerVertical fullWidth spacing={INNER_SPACING}>
        <FlexContainerVertical fullWidth spacing={TITLE_SPACING}>
          <Typography variant="h5">{t('Signers')}</Typography>
          <Typography>
            {t(
              'Sign Document sub-tasks can be assigned to these individuals in Dado. The start of these tasks will trigger the sending of the Docusign envelope.'
            )}
          </Typography>
        </FlexContainerVertical>
        <SignerRolesSelector
          value={value}
          onChange={onChange}
          actorDescriptors={Object.values(actors)}
        />
      </FlexContainerVertical>

      <FlexContainerVertical fullWidth spacing={INNER_SPACING}>
        <FlexContainerVertical fullWidth spacing={TITLE_SPACING}>
          <Typography variant="h5">{t('Data to be inserted')}</Typography>
          <Typography>
            {t(
              'Select which data from Dado should be inserted into your Docusign template, to personalize it for the signer(s).'
            )}
          </Typography>
          <MappingsSelector
            tpl={tpl}
            value={value}
            onChange={onChange}
            customFields={customFields}
            actors={actors}
          />
        </FlexContainerVertical>
      </FlexContainerVertical>

      {!!tpl.tpl.ccs.length && (
        <FlexContainerVertical fullWidth spacing={INNER_SPACING}>
          <FlexContainerVertical fullWidth spacing={TITLE_SPACING}>
            <Typography variant="h5">{t('Other Recipients')}</Typography>
            <Typography>
              {t(
                'These individuals will receive notifications as specified in the template settings in Docusign. Sign Document sub-tasks cannot be assigned to them.'
              )}
            </Typography>
          </FlexContainerVertical>
          <OtherRolesSelector
            value={value}
            onChange={onChange}
            actorDescriptors={Object.values(actors)}
            otherRoles={{ ccs: tpl.tpl.ccs }}
          />
        </FlexContainerVertical>
      )}
    </FlexContainerVertical>
  );
};

const toEmptyMapping = (t: DocuSignTab) => ({
  actor: '',
  dadoKey: '',
  fallback: '',
  tabId: t.tabId,
  tabLabel: t.tabLabel
});

const TemplateConfigurator = (props: {
  orgId: string;
  value: IDocuSignEnvelopeTemplate;
  onChange: (nextValue: IDocuSignEnvelopeTemplate) => void;
  customFields: CustomUserField[];
  supportingActors: { [actorKey: string]: ISupportingActor };
  mode: 'create' | 'edit';
}) => {
  const { value, orgId } = props;
  const [tpl, loading] = useDocuSignEnvelopeTemplate(
    orgId,
    value.docuSignEnvelopeTemplateId
  );
  if (loading) {
    return <Loader height={200} />;
  }
  if (!tpl) {
    return null;
  }
  return <TemplateConfiguratorInner tpl={tpl} {...props} />;
};

const isRecipientComplete = (r: DocuSignRecipientWithActor) =>
  r.actor.key && r.actor.emailField && r.recipientId && r.roleName;

export const EditDocuSignEnvelopeResourceDialog = ({
  open,
  onClose,
  orgId,
  value,
  onChangeAsync,
  onChange,
  customFields,
  supportingActors,
  mode
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  value: IDocuSignEnvelopeTemplate;
  onChangeAsync?: (nextResource: IDocuSignEnvelopeTemplate) => Promise<any>;
  onChange?: (nextResource: IDocuSignEnvelopeTemplate) => void;
  customFields: CustomUserField[];
  supportingActors: { [actorKey: string]: ISupportingActor };
  mode: 'create' | 'edit';
}) => {
  const { t } = useUnsafeTranslation();

  const [tpls, loadingTpls, errorTpls] = useDocuSignEnvelopeTemplates(orgId);
  console.log('TPLS', tpls);

  const [model, setModel] = useState(value);

  console.log(model);

  const areAllSignersComplete =
    model.signers.length && model.signers.every(isRecipientComplete);
  const areAllOtherRecipientsComplete =
    !model.ccs.length || model.ccs.every(isRecipientComplete);
  const areAllMappingsComplete =
    !model.mappings.length ||
    model.mappings.every((m) => m.actor && m.dadoKey && m.tabId && m.tabLabel);
  const isSaveDisabled =
    model === value ||
    !areAllSignersComplete ||
    !areAllOtherRecipientsComplete ||
    !areAllMappingsComplete;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitleWithTypography>
        {t('Configure Docusign template')}
      </DialogTitleWithTypography>
      <DialogContent>
        <FlexContainerVertical fullWidth spacing={OUTER_SPACING}>
          {loadingTpls && (
            <SelectCustom fullWidth label={'Select Template'} loading />
          )}
          {tpls && !loadingTpls && (
            <TemplateSelector
              label={t('Select Template')}
              value={model.docuSignEnvelopeTemplateId}
              onChange={(nextValue) =>
                setModel((x) => ({
                  ...x,
                  docuSignEnvelopeTemplateId: nextValue?.id || '',
                  name: nextValue?.name || '',
                  signers:
                    nextValue?.signers.map((s) => {
                      return {
                        actor: {
                          type: 'actor',
                          emailField: '',
                          key: '',
                          optional: false
                        },
                        roleName: s.roleName,
                        recipientId: s.recipientId
                      };
                    }) || [],
                  ccs: [],
                  mappings: []
                }))
              }
              options={tpls}
            />
          )}
          {model.docuSignEnvelopeTemplateId && (
            <TemplateConfigurator
              orgId={orgId}
              value={model}
              onChange={setModel}
              supportingActors={supportingActors}
              customFields={customFields}
              mode={mode}
            />
          )}
        </FlexContainerVertical>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        {onChange && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => onChange(model)}
            disabled={isSaveDisabled}
          >
            {t('Save')}
          </Button>
        )}
        {onChangeAsync && (
          <ButtonWithPromise
            variant="contained"
            color="primary"
            pending={t('Saving...')}
            onClick={() => onChangeAsync(model)}
            disabled={isSaveDisabled}
          >
            {t('Save')}
          </ButtonWithPromise>
        )}
      </DialogActions>
    </Dialog>
  );
};

const updateResource = (
  resources: IExperienceTemplateResource[],
  nextResource: IDocuSignEnvelopeTemplate
): IExperienceTemplateResource[] => {
  const nextResources = [...resources];
  const prevResI = nextResources.findIndex(
    (r) => r.resourceId === nextResource.resourceId
  );
  if (prevResI !== -1) {
    nextResources[prevResI] = nextResource;
  } else {
    nextResources.push(nextResource);
  }
  return nextResources;
};

const RedText = styled('div')((p) => ({
  color: p.theme.palette.error.main
}));

const WhenResourcesArePresent = ({
  value,
  onChange,
  actor,
  selectedResource,
  allResources,
  onRequestCreateNewResource,
  onRequestEditResource,
  tokens
}: {
  value: ISubTaskDocuSignEnvelopeSignTemplate;
  onChange: (nextValue: ISubTaskDocuSignEnvelopeSignTemplate) => void;
  actor: ActorDescriptor | null;
  selectedResource: IDocuSignEnvelopeTemplate | null;
  allResources: IDocuSignEnvelopeTemplate[];
  onRequestCreateNewResource: () => void;
  onRequestEditResource: () => void;
  tokens: TokenDefinition[];
}) => {
  const SHOW_DESCRIPTION = true;
  const { t } = useUnsafeTranslation();
  const roleNames =
    (actor &&
      selectedResource?.signers
        .filter((s) => s.actor.key === actor.key)
        .map((x) => x.roleName)) ||
    [];
  return (
    <FlexContainerVertical fullWidth spacing={3}>
      {SHOW_DESCRIPTION && (
        <MarkdownEditorWithReplacementTokens
          fullWidth
          value={value.data.description.t}
          onChange={(v) => {
            const nextValue: ISubTaskDocuSignEnvelopeSignTemplate = Object.assign(
              {},
              value,
              {
                data: Object.assign({}, value.data, {
                  description: Object.assign({}, value.data.description, {
                    ...value.data.description,
                    t: v
                  })
                })
              }
            );
            onChange(nextValue);
          }}
          tokens={tokens}
        />
      )}
      <FlexContainerVertical fullWidth>
        <ResourceSelector
          value={selectedResource}
          onChange={(nextResource) => {
            if (!nextResource) {
              return;
            }
            onChange({
              ...value,
              data: {
                ...value.data,
                resourceId: nextResource.resourceId
              }
            });
          }}
          options={allResources}
          onRequestCreateNewResource={onRequestCreateNewResource}
        />

        {selectedResource && (
          <FlexContainer justifyContent="flex-end" fullWidth>
            <Button
              color="primary"
              size="small"
              endIcon={<ExternalLink size={14} />}
              onClick={() => onRequestEditResource()}
            >
              {t('Template Settings')}
            </Button>
          </FlexContainer>
        )}
      </FlexContainerVertical>

      {!!roleNames.length && actor && (
        <LightMdToJsx>
          {t(
            'The **{{ actor }}** will be assigned as **{{ roles }}** in Docusign. Change this in the template settings.',
            {
              actor: actor.label,
              roles: joinWithCommaAndAnd(roleNames, t)
            }
          )}
        </LightMdToJsx>
      )}
      {!roleNames.length && actor && (
        <RedText>
          <LightMdToJsx>
            {t(
              '**{{ actor }}** is not assigned as a Signer for this Docusign template. You can change this in the template settings.',
              {
                actor: actor.label
              }
            )}
          </LightMdToJsx>
        </RedText>
      )}
    </FlexContainerVertical>
  );
};

export const BlockSettingsDocuSignEnvelopeSign = ({
  value,
  onChange,
  onChangeResources,
  actor,
  validation,
  setValidation,
  expTpl,
  customFields,
  resources,
  goToSubPage,
  tokens
}: {
  value: ISubTaskDocuSignEnvelopeSignTemplate;
  onChange: (nextValue: ISubTaskDocuSignEnvelopeSignTemplate) => void;
  actor: string;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  const { orgId } = expTpl;
  const actorDescriptors = useActorDescriptorsFromTemplate(
    expTpl.supportingActors
  );
  const actorDescriptor = useMemo<ActorDescriptor | null>(
    () => actorDescriptors[actor] || null,
    [actorDescriptors, actor]
  );
  const [dialogData, setDialogData] = useState<{
    resource: IDocuSignEnvelopeTemplate;
    mode: 'create' | 'edit';
  } | null>(null);
  const selectedResource =
    (value.data.resourceId &&
      resources.find(
        (r): r is IDocuSignEnvelopeTemplate =>
          r.type === TemplateResourceType.DOCUSIGN_ENVELOPE_TEMPLATE &&
          r.resourceId === value.data.resourceId
      )) ||
    null;

  const otherResources = resources.filter(
    (r): r is IDocuSignEnvelopeTemplate =>
      r.type === TemplateResourceType.DOCUSIGN_ENVELOPE_TEMPLATE
  );

  const onCreateNewResource = () => {
    setDialogData({
      mode: 'create',
      resource: {
        resourceId: shortid(),
        status: 'COMPLETE',
        type: TemplateResourceType.DOCUSIGN_ENVELOPE_TEMPLATE,
        docuSignEnvelopeTemplateId: '',
        name: '',
        mappings: [],
        signers: [],
        ccs: []
      }
    });
  };

  const _updateResource = (nextResource: IDocuSignEnvelopeTemplate) => {
    onChangeResources(updateResource(resources, nextResource));
  };

  // Template picker

  // Allow to select a resource or create a new one
  // This is the first picker
  // Creating a new one brings up the resource dialog, which then also
  // contains a Docusign template picker

  // If there is no such resource, then immediately invite to create

  // Make sure that no other task can also write to this resource? Not clear...
  return (
    <>
      {!otherResources.length && (
        <Centered height={200}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => onCreateNewResource()}
          >
            {t('Set up DocuSign Template')}
          </Button>
        </Centered>
      )}
      {!!otherResources.length && (
        <WhenResourcesArePresent
          value={value}
          onChange={onChange}
          actor={actorDescriptor}
          selectedResource={selectedResource}
          allResources={otherResources}
          onRequestCreateNewResource={() => onCreateNewResource()}
          onRequestEditResource={() =>
            selectedResource &&
            setDialogData({ resource: selectedResource, mode: 'edit' })
          }
          tokens={tokens}
        />
      )}
      {dialogData && (
        <EditDocuSignEnvelopeResourceDialog
          open={true}
          onClose={() => setDialogData(null)}
          orgId={orgId}
          value={dialogData.resource}
          mode={dialogData.mode}
          onChange={(nextResource) => {
            _updateResource(nextResource);
            onChange({
              ...value,
              data: {
                ...value.data,
                resourceId: nextResource.resourceId
              }
            });
            setDialogData(null);
          }}
          customFields={customFields}
          supportingActors={expTpl.supportingActors}
        />
      )}
    </>
  );
};

export const BlockSettingsDocuSignEnvelopeSignReadOnly = ({
  value,
  resources,
  expTpl,
  customFields,
  showOnCompleteHookSettings,
  tokens
}: {
  value: ISubTaskDocuSignEnvelopeSignTemplate;
  resources: IExperienceTemplateResource[];
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  showOnCompleteHookSettings: boolean;
  tokens: TokenDefinition[];
}) => {
  return null;
};

// Next steps
// - Show proper warning if misconfigured sub task
// - Deal with validation somehow
// - Read only state
// - Update mappings
// - Make sure we detect if a CC recipient is prefilled and therefore can't be handled by us
